import { BookingNoteFormDialogContents, BookingPageFormDialogContents, BookingPagesView } from "@tellescope/calendar";
import { CalendarPageTabsHeader } from "./CalendarPageTabsHeader";
import { useCallback, useState } from "react";
import { Button, DestructiveConfirmDialog, Dialog } from "@tellescope/ui";
import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSidebarShallowStore } from "../../store/sidebar-store";
import { useCalendarShallowStore } from "../../store";

export const CalendarBookingPagesPage = () => {
  const { t } = useTranslation("calendar");
  const [bookingPageBeingDeleted, setBookingPageBeingDeleted] =
    useState<TellescopeModelFromName<"appointment_booking_pages">>();
  const [search, setSearch] = useSearchParams();
  const bookingPageIdBeingDuplicated = search.get("duplicateBookingPageId") ?? undefined;
  const bookingPageIdBeingEdited = search.get("bookingPageId") ?? bookingPageIdBeingDuplicated ?? undefined;
  const enduserBookingNotePageId = search.get("enduserBookingNotePageId") ?? undefined;
  const [isCreating, setIsCreating] = useState(false);
  const { openSidebar } = useSidebarShallowStore("openSidebar");
  const { showArchivedBookingPages, toggleShowArchivedBookingPages } = useCalendarShallowStore(
    "showArchivedBookingPages",
    "toggleShowArchivedBookingPages"
  );

  const deleteBookingPageMutation = useTellescopeMutation({
    model: "appointment_booking_pages",
    method: "deleteOne",
    onSuccess: () => setBookingPageBeingDeleted(undefined),
    onError: () => toast.error(t("Error deleting booking page, please try again")),
  });

  const bookingPageQuery = useTellescopeQuery({
    model: "appointment_booking_pages",
    method: "getOne",
    request: bookingPageIdBeingEdited ?? "",
    enabled: !!bookingPageIdBeingEdited,
  });

  const closeDialog = useCallback(() => {
    setSearch(
      (prev) => {
        prev.delete("bookingPageId");
        prev.delete("duplicateBookingPageId");
        prev.delete("enduserBookingNotePageId");
        return prev;
      },
      { replace: true }
    );
    setIsCreating(false);
  }, [setSearch]);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <CalendarPageTabsHeader />
      <BookingPagesView
        getBookingPageOnClick={(bookingPage) => ({
          to: `.?bookingPageId=${bookingPage.id}`,
          replace: true,
        })}
        onDelete={(bookingPage) => setBookingPageBeingDeleted(bookingPage)}
        onAddBookingPageClick={() => {
          setIsCreating(true);
        }}
        getBookingPageOnDuplicate={(bookingPage) => ({
          to: `.?duplicateBookingPageId=${bookingPage.id}`,
          replace: true,
        })}
        onCopyLink={() => toast.success(t("Booking link copied!"))}
        getBookingPageOnCreateNote={(bookingPage) => ({
          to: `.?enduserBookingNotePageId=${bookingPage.id}`,
        })}
        showArchived={showArchivedBookingPages}
        toggleShowArchived={toggleShowArchivedBookingPages}
      />
      <DestructiveConfirmDialog
        open={!!bookingPageBeingDeleted}
        onOpenChange={(open) => {
          if (!open) {
            setBookingPageBeingDeleted(undefined);
          }
        }}
        onConfirm={() => {
          if (bookingPageBeingDeleted?.id) deleteBookingPageMutation.mutateAsync(bookingPageBeingDeleted?.id);
        }}
        onCancel={() => setBookingPageBeingDeleted(undefined)}
        loading={deleteBookingPageMutation.isPending}
        title={t("Delete booking page?")}
        confirmButtonTestId="booking-page-delete-confirm"
        description={
          bookingPageBeingDeleted?.title
            ? t('"{{bookingPageName}}" will be permanently removed. This cannot be undone.', {
                bookingPageName: bookingPageBeingDeleted?.title,
              })
            : t("This booking page will be permanently removed. This cannot be undone.")
        }
      />
      <Dialog
        open={bookingPageIdBeingEdited ? !!bookingPageQuery.data : isCreating}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
      >
        <BookingPageFormDialogContents
          bookingPage={bookingPageIdBeingEdited ? bookingPageQuery.data : undefined}
          onCancel={() => {
            closeDialog();
          }}
          onSave={() => {
            closeDialog();
          }}
          shouldDuplicate={bookingPageIdBeingEdited === bookingPageIdBeingDuplicated}
        />
      </Dialog>
      <Dialog
        open={!!enduserBookingNotePageId}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
      >
        <BookingNoteFormDialogContents
          bookingPageId={enduserBookingNotePageId ?? ""}
          onCancel={closeDialog}
          onSave={(enduserId) => {
            closeDialog();
            toast.success(
              <div className="flex justify-between items-center w-[300px]">
                <span>{t("Booking note added")}</span>
                <Button
                  variant="ghost"
                  onClick={() => {
                    openSidebar("ContactDetails", { enduserId });
                  }}
                >
                  {t("View Patient")}
                </Button>
              </div>
            );
          }}
          onError={() => toast.error(t("Error adding booking note, please try again"))}
        />
      </Dialog>
    </div>
  );
};
